import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import logoLight from '../assets/images/logo-white.svg';   // ← SWAPPED: White logo for light backgrounds
import logoDark from '../assets/images/logo.svg';          // ← SWAPPED: Dark logo for dark backgrounds

interface HeaderProps {
  onMenuClick?: () => void;
}

export default function Header({ onMenuClick }: HeaderProps) {
  const { instance, accounts } = useMsal();
  const { theme, toggleTheme } = useTheme();
  const { isMobile } = useMediaQuery();
  const navigate = useNavigate();

  const handleLogout = () => {
    instance.logoutRedirect({
      account: accounts[0] ?? null,
      logoutHint: accounts[0]?.username ?? undefined,
      postLogoutRedirectUri: '/',
    });
  };

  const userName = accounts[0]?.name || accounts[0]?.username || 'User';

  // Check if background is dark
  const isDarkMode = parseInt(theme.colors.bgPrimary.replace('#', ''), 16) < 0x808080;
  
  // Choose logo based on theme
  // Dark background → use light/white logo
  // Light background → use dark logo
  const logoSrc = isDarkMode ? logoDark : logoLight;

  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: isMobile ? '12px 16px' : '16px 24px',
        background: theme.colors.bgSecondary,
        borderBottom: `1px solid ${theme.colors.border}`,
        transition: 'all 0.3s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        {/* Hamburger Menu Button (Mobile Only) */}
        {isMobile && (
          <button
            onClick={onMenuClick}
            style={{
              background: 'transparent',
              border: 'none',
              color: theme.colors.textPrimary,
              fontSize: '24px',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              transition: 'background 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = theme.colors.hover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
            aria-label="Open menu"
          >
            ☰
          </button>
        )}

        {/* Logo/Title */}
        <div 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            cursor: 'pointer'
          }}
          onClick={() => navigate('/home')}
        >
          <img 
            src={logoSrc}
            alt="Skydrive Logo"
            style={{ 
              height: isMobile ? '32px' : '50px',
              width: 'auto',
              maxWidth: '180px',
              objectFit: 'contain',
              transition: 'all 0.3s ease'
            }} 
          />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '16px' }}>
        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          style={{
            background: 'transparent',
            border: `1px solid ${theme.colors.border}`,
            borderRadius: '6px',
            padding: isMobile ? '6px 10px' : '8px 12px',
            color: theme.colors.textPrimary,
            cursor: 'pointer',
            fontSize: isMobile ? '18px' : '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = theme.colors.hover;
            e.currentTarget.style.borderColor = theme.colors.brand;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = theme.colors.border;
          }}
          title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        >
          {isDarkMode ? '☀️' : '🌙'}
        </button>

        {/* User Info */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: isMobile ? '6px 10px' : '8px 12px',
            background: theme.colors.bgPrimary,
            borderRadius: '6px',
            border: `1px solid ${theme.colors.border}`,
          }}
        >
          <div style={{ 
            display: isMobile ? 'none' : 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end'
          }}>
            <span
              style={{
                fontSize: '14px',
                fontWeight: 500,
                color: theme.colors.textPrimary,
                transition: 'color 0.3s ease',
              }}
            >
              {userName.split(' ')[0]}
            </span>
          </div>
          <div
            style={{
              width: isMobile ? '32px' : '36px',
              height: isMobile ? '32px' : '36px',
              borderRadius: '50%',
              background: theme.gradients.brand,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 600,
              fontSize: isMobile ? '14px' : '16px',
            }}
          >
            {userName.charAt(0).toUpperCase()}
          </div>
        </div>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          style={{
            background: 'transparent',
            border: `1px solid ${theme.colors.border}`,
            borderRadius: '6px',
            padding: isMobile ? '6px 12px' : '8px 16px',
            color: theme.colors.textPrimary,
            cursor: 'pointer',
            fontSize: isMobile ? '13px' : '14px',
            fontWeight: 500,
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = theme.colors.error;
            e.currentTarget.style.borderColor = theme.colors.error;
            e.currentTarget.style.color = '#fff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = theme.colors.border;
            e.currentTarget.style.color = theme.colors.textPrimary;
          }}
        >
          {isMobile ? '🚪' : 'Logout'}
        </button>
      </div>
    </header>
  );
}
